import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { breakpointValue, ifThen, otherwise } from '@cpangular/rxjs';
import { LoadMoreComponent } from 'common-ui';
import { ProductId } from 'products-data';
import { Observable } from 'rxjs';
import { SearchModel } from 'state-data';
import { ProductListingLayoutComponent } from '../product-listing-layout';
import { ProductResultListComponent } from '../product-result-list';

@Component({
  selector: 'ideal-product-listing',
  imports: [LoadMoreComponent, MatButtonToggleModule, MatIconModule, ProductResultListComponent, ProductListingLayoutComponent],
  standalone: true,
  templateUrl: './product-listing.component.html',
  styleUrl: './product-listing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.view]': 'effectiveView()',
    '[attr.h-size]': 'componentWidth()',
  },
})
export class ProductListingComponent {
  private readonly _element = inject(ElementRef<HTMLElement>);

  public readonly floatHeader = input(false);
  public readonly result = input<SearchModel<{ id: ProductId }> | null>(null);
  public readonly loading = input<boolean>(false);
  public readonly loadMoreMode = input<'automatic' | 'manual'>('automatic');
  public readonly view = model<'grid' | 'list'>('grid');
  public readonly loadMore = output<void>();

  protected readonly resultIds = computed(() => this.result()?.results?.map((r) => r?.id ?? null) ?? []);
  protected readonly hasMore = computed(() => this.result()?.moreResults ?? false);
  protected readonly hasResults = computed(() => (this.result()?.results?.length ?? 0) > 0);

  protected readonly componentWidth = toSignal(
    breakpointValue(
      this._element.nativeElement,
      ifThen((size) => size.width < 350, 'sm'),
      ifThen((size) => size.width < 800, 'md'),
      ifThen((size) => size.width < 1200, 'lg'),
      otherwise('xl'),
    ) as Observable<'sm' | 'md' | 'lg' | 'xl'>,
    { initialValue: 'sm' },
  );

  protected readonly forceGridView = computed(() => ['sm', 'md'].includes(this.componentWidth()));
  protected readonly effectiveView = computed(() => (this.forceGridView() ? 'grid' : this.view()));

  protected setView(view: string) {
    this.view.set(view === 'list' ? 'list' : 'grid');
  }

  protected onLoadMore() {
    this.loadMore.emit();
  }
}
